import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { TrlPriceType } from '»components/atoms';
import { TrlDateFormatted, TrlTimeFormatted } from '»utils/date';
import { TrlFormattedMessagePlain } from '»utils/i18n';
import { formatPrimaryTitlePrice, useTrancheTypeLabel } from '»utils/index';
import { TrlNumberFormatted } from '»utils/number';
import { trlFieldDefaultValue } from '»utils/trlFieldDefaultValue';

import {
  BoxContainerStyled,
  BoxValuesStyled,
  ContainerLeftStyled,
  LabelHeaderStyled,
  LabelStyled,
  LabelValueStyled,
} from './TrlPrimaryMarketIssue.styles';

export const TrlPrimaryMarketIssue = ({ dataItem }) => {
  const { getTrancheTypeLabel } = useTrancheTypeLabel();

  const {
    offerType,
    publicOfferId,
    priceType,
    revokeDate,
    operationDate,
    settlementDate,
    subscriptionDate,
    offerMinPrice,
    offerMaxPrice,
    currency,
    instrumentId,
    maxQuantity,
    minimumBatch,
    minQuantity,
    price,
    minPrice,
    maxPrice,
    trancheType,
    maxAmount,
    minAmount,
    endDate,
    typeNegotiation,
  } = dataItem;

  const trancheTypeLabel = getTrancheTypeLabel(trancheType);

  return (
    <BoxContainerStyled id="divEmissao">
      <LabelHeaderStyled>
        <FormattedMessage id="TRLT_UI-ISSUANCE" />
      </LabelHeaderStyled>
      <BoxValuesStyled>
        <ContainerLeftStyled>
          <LabelStyled>
            <FormattedMessage id="TRLT_UI-ID" />
          </LabelStyled>
          <LabelValueStyled>{publicOfferId}</LabelValueStyled>
          <LabelStyled>
            <FormattedMessage id="TRLT_UI-TRANCHE" />
          </LabelStyled>
          <LabelValueStyled>{trancheTypeLabel}</LabelValueStyled>
          <LabelStyled>
            <FormattedMessage id="TRLT_UI-INSTRUMENT" />
          </LabelStyled>
          <LabelValueStyled>{instrumentId}</LabelValueStyled>
          <LabelStyled>
            <FormattedMessage id="TRLT_UI-CURRENCY" />
          </LabelStyled>
          <LabelValueStyled>{currency}</LabelValueStyled>
          <LabelStyled>
            <FormattedMessage id="TRLT_UI-END_INTENTION" />
          </LabelStyled>
          <LabelValueStyled>
            <TrlDateFormatted date={endDate} />
            {' | '}
            <TrlTimeFormatted value={endDate} hour="numeric" minute="numeric" />
          </LabelValueStyled>
        </ContainerLeftStyled>
        <ContainerLeftStyled>
          <LabelStyled>
            <FormattedMessage id="TRLT_UI-SUBSCRIPTION" />
          </LabelStyled>
          <LabelValueStyled>
            <TrlDateFormatted date={operationDate} />
            {' | '}
            <TrlTimeFormatted
              value={operationDate}
              hour="numeric"
              minute="numeric"
            />
          </LabelValueStyled>
          <LabelStyled>
            <FormattedMessage id="TRLT_UI-BUSINESS_ADMISSION" />
          </LabelStyled>
          <LabelValueStyled>
            <TrlDateFormatted date={subscriptionDate} />
          </LabelValueStyled>
          <LabelStyled>
            <FormattedMessage id="TRLT_UI-LIQUIDATION" />
          </LabelStyled>
          <LabelValueStyled>
            <TrlDateFormatted date={settlementDate} />
          </LabelValueStyled>
          <LabelStyled>
            <FormattedMessage id="TRLT_UI-MODIFICATION_LIMIT" />
          </LabelStyled>
          <LabelValueStyled>
            <TrlDateFormatted date={revokeDate} />
            {' | '}
            <TrlTimeFormatted
              value={revokeDate}
              hour="numeric"
              minute="numeric"
            />
          </LabelValueStyled>
          <LabelStyled>
            <FormattedMessage id="TRLT_UI-PRICE_TYPE" />
          </LabelStyled>
          <LabelValueStyled>
            <TrlPriceType value={priceType} />
          </LabelValueStyled>
        </ContainerLeftStyled>
        <ContainerLeftStyled>
          <LabelStyled>
            <FormattedMessage id="TRLT_UI-OFFER_TYPE" />
          </LabelStyled>
          <LabelValueStyled>{offerType}</LabelValueStyled>
          <LabelStyled>
            <FormattedMessage
              id={
                trancheType === 'R'
                  ? 'TRLT_UI-MINIMUM_AMOUNT'
                  : 'TRLT_UI-MINIMUM_QTY'
              }
            />
          </LabelStyled>
          <LabelValueStyled>
            <TrlNumberFormatted
              value={trancheType === 'R' ? minAmount : minQuantity}
            />
          </LabelValueStyled>
          <LabelStyled>
            <FormattedMessage
              id={
                trancheType === 'R'
                  ? 'TRLT_UI-MAX_AMOUNT'
                  : 'TRLT_UI-MAXIMUM_QTY'
              }
            />
          </LabelStyled>
          <LabelValueStyled>
            <TrlNumberFormatted
              value={trancheType === 'R' ? maxAmount : maxQuantity}
            />
          </LabelValueStyled>
          <LabelStyled>
            <FormattedMessage id="TRLT_UI-QTY_MULTIPLES" />
          </LabelStyled>
          <LabelValueStyled>
            <TrlNumberFormatted value={minimumBatch} />
          </LabelValueStyled>
        </ContainerLeftStyled>
        <ContainerLeftStyled>
          <LabelStyled>
            {TrlFormattedMessagePlain({
              id: `${priceType === 'T' ? 'TRLT_UI-RATE' : 'TRLT_UI-PRICE'}`,
            })}
          </LabelStyled>
          <LabelValueStyled>
            {(priceType === 'Q' && typeNegotiation !== 'Q') ||
            priceType === 'T' ? (
              <TrlNumberFormatted
                value={price ?? trlFieldDefaultValue()}
                endDecorator=" %"
              />
            ) : (
              <TrlNumberFormatted
                value={price ?? trlFieldDefaultValue()}
                decimalDigits={2}
              />
            )}
          </LabelValueStyled>
          <LabelStyled>
            {TrlFormattedMessagePlain({
              id: `${
                priceType === 'T'
                  ? 'TRLT_UI-MIN_INTEREST_RATE'
                  : 'TRLT_UI-MINIMUM_PRICE'
              }`,
            })}
          </LabelStyled>
          <LabelValueStyled>
            {formatPrimaryTitlePrice({
              trancheType,
              priceType,
              price,
              minMaxPrice: minPrice,
              offerPrice: offerMinPrice,
              decimalDigits: 2,
              typeNegotiation,
            })}
          </LabelValueStyled>
          <LabelStyled>
            {TrlFormattedMessagePlain({
              id: `${
                priceType === 'T'
                  ? 'TRLT_UI-MAX_INTEREST_RATE'
                  : 'TRLT_UI-MAXIMUM_PRICE'
              }`,
            })}
          </LabelStyled>
          <LabelValueStyled>
            {formatPrimaryTitlePrice({
              trancheType,
              priceType,
              price,
              minMaxPrice: maxPrice,
              offerPrice: offerMaxPrice,
              decimalDigits: 2,
              typeNegotiation,
            })}
          </LabelValueStyled>
        </ContainerLeftStyled>
      </BoxValuesStyled>
    </BoxContainerStyled>
  );
};

TrlPrimaryMarketIssue.propTypes = {
  dataItem: PropTypes.object,
};
